import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, MouseEvent, ReactNode } from "react";

export interface DragItem {
  id: string;
  [key: string]: any;
}

export type DragType = "MOVING" | "DROP";

export type DragSubscriber = (item: DragItem, type: DragType, date: string, startDate: string) => void;

type DragClickHandler = (item: DragItem, date: string) => void;

interface CustomDragContextValue {
  item: DragItem | null;
  targetDate: string | null;
  disabled: boolean;
  // called when a drag starts on an item
  onStart: (item: DragItem, startDate: string, onClick?: DragClickHandler) => void;
  // called when mouse enters a drop target while dragging
  onTargetChange: (date: string) => void;
  // called when mouse is released on a drop target
  onEnd: (date: string) => void;
  // subscribe to drag events
  subscribe: (subscriber: DragSubscriber) => () => void;
}

const CustomDragContext = createContext<CustomDragContextValue | undefined>(undefined);

function useCustomDragContext(): CustomDragContextValue {
  const ctx = useContext(CustomDragContext);
  if (!ctx) {
    throw new Error("useCustomDrag/useCustomDrop must be used inside CustomDragProvider");
  }
  return ctx;
}

export function CustomDragProvider({
  children,
  disabled = false,
  clickThresholdMs = 200,
}: {
  children: ReactNode;
  disabled?: boolean;
  clickThresholdMs?: number;
}) {
  const [item, setItem] = useState<DragItem | null>(null);
  const [targetDate, setTargetDate] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string | null>(null);

  const subscribersRef = useRef<DragSubscriber[]>([]);
  const dragStartTimeRef = useRef<number | null>(null);
  const clickHandlerRef = useRef<DragClickHandler | undefined>(undefined);

  const resetDragState = useCallback(() => {
    setItem(null);
    setStartDate(null);
    setTargetDate(null);
    dragStartTimeRef.current = null;
    clickHandlerRef.current = undefined;
  }, []);

  const notify = useCallback((dragItem: DragItem, type: DragType, date: string, start: string) => {
    subscribersRef.current.forEach((fn) => fn(dragItem, type, date, start));
  }, []);

  const onStart = useCallback(
    (dragItem: DragItem, start: string, onClick?: DragClickHandler) => {
      if (disabled) return;
      setItem(dragItem);
      setStartDate(start);
      setTargetDate(null);
      dragStartTimeRef.current = Date.now();
      clickHandlerRef.current = onClick;
    },
    [disabled],
  );

  const onTargetChange = useCallback(
    (date: string) => {
      if (disabled) return;
      if (!item || !startDate) return;
      setTargetDate(date);
      notify(item, "MOVING", date, startDate);
    },
    [item, startDate, notify, disabled],
  );

  const onEnd = useCallback(
    (date: string) => {
      if (!item || !startDate) {
        resetDragState();
        return;
      }

      const startTime = dragStartTimeRef.current;
      const endTime = Date.now();
      const duration = typeof startTime === "number" ? endTime - startTime : Number.MAX_SAFE_INTEGER;

      // Always notify subscribers of the DROP
      notify(item, "DROP", date, startDate);

      // If drop date equals startDate and drag was short, treat as click
      if (date === startDate && duration <= clickThresholdMs && clickHandlerRef.current) {
        clickHandlerRef.current(item, startDate);
      }

      resetDragState();
    },
    [item, startDate, notify, clickThresholdMs, resetDragState],
  );

  const subscribe = useCallback((subscriber: DragSubscriber) => {
    subscribersRef.current.push(subscriber);
    return () => {
      subscribersRef.current = subscribersRef.current.filter((fn) => fn !== subscriber);
    };
  }, []);

  const value = useMemo(
    () => ({
      item,
      targetDate,
      disabled,
      onStart,
      onTargetChange,
      onEnd,
      subscribe,
    }),
    [item, targetDate, disabled, onStart, onTargetChange, onEnd, subscribe],
  );

  return <CustomDragContext.Provider value={value}>{children}</CustomDragContext.Provider>;
}

// Draggable hook
export function useCustomDrag({ item, date, onClick }: { item: DragItem; date: string; onClick?: DragClickHandler }) {
  const { item: currentItem, disabled, onStart } = useCustomDragContext();

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (disabled) return;
      onStart(item, date, onClick);
    },
    [disabled, onStart, item, date, onClick],
  );

  return {
    element: {
      onMouseDown: disabled ? undefined : handleMouseDown,
    },
    isDragging: !!currentItem && currentItem.id === item.id,
  };
}

// Subscriber hook
export function useDragSubscriber(subscriber: DragSubscriber) {
  const ctx = useCustomDragContext();

  useEffect(() => {
    const unsubscribe = ctx.subscribe(subscriber);
    return unsubscribe;
  }, [ctx, subscriber]);
}

// Drop target hook
export function useCustomDrop({ date }: { date: string }) {
  const { item, disabled, onTargetChange, onEnd } = useCustomDragContext();
  const isDragging = !!item && !disabled;

  const handleMouseEnter = useCallback(() => {
    if (!isDragging) return;
    onTargetChange(date);
  }, [isDragging, onTargetChange, date]);

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;
    onEnd(date);
  }, [isDragging, onEnd, date]);

  return {
    isDragging,
    element: {
      onMouseEnter: isDragging ? handleMouseEnter : undefined,
      onMouseUp: isDragging ? handleMouseUp : undefined,
    },
  };
}
